import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';

import AdminLayout from './components/AdminLayout';
import { EnvironmentBanner } from './components/EnvironmentBanner';
import { FullPageLoading } from './components/FullPageLoading';
import Layout from './components/Layout';
import { OfflineBanner } from './components/OfflineBanner';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RoleGuard } from './components/RoleGuard';
import { UpdateAvailable } from './components/UpdateAvailable';
import { Spinner } from './components/ui/spinner';
import { TooltipProvider } from './components/ui/tooltip';
import { ClientSyncProvider } from './context/ClientSyncProvider';
import { NotificationProvider } from './context/NotificationProvider';
import { ThemeProvider } from './context/ThemeProvider';
import { useAuth } from './hooks/useAuth';
import CalendarPage from './pages/CalendarPage';
import CallbackPage from './pages/CallbackPage';
import CertificatesPage from './pages/CertificatesPage';
import ComunicacoesPage from './pages/ComunicacoesPage';
import DashboardPage from './pages/dashboard';
import DocumentsPage from './pages/DocumentsPage';
import EscritorioPage from './pages/escritorios/EscritorioPage';
import EscritoriosListaPage from './pages/escritorios/EscritoriosListaPage';
import BlockedPage from './pages/BlockedPage';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ModelsPage from './pages/modelos';
import PersonalizacaoIaPage from './pages/PersonalizacaoIaPage';
import SecretarioPage from './pages/secretario';
import AtendimentoPage from './pages/secretario/AtendimentoPage';
import TimbradoPage from './pages/TimbradoPage';
import FeriadosPage from './pages/feriados/FeriadosPage';
import ClientesPage from './pages/clientes';
import ClienteDetailPage from './pages/clientes/ClienteDetailPage';
import ClienteFormPage from './pages/clientes/form';
import ProcessesPage from './pages/processos';
import RedirecionarPeticionar from './pages/processos/autos/protocolo/RedirecionarPeticionar';

// Lazy-load: Stripe SDK (~40KB) só carrega quando o usuário acessa o onboarding
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const AutosPage = lazy(() => import('./pages/processos/autos'));
// O editor forense (TipTap e o corretor) só desce quando se abre um modelo.
const ModeloEditorPage = lazy(() => import('./pages/modelos/ModeloEditorPage'));
const DocumentoPage = lazy(() => import('./pages/processos/autos/DocumentoPage'));
const DossiePage = lazy(() => import('./pages/processos/dossie'));
const PeticoesPage = lazy(() => import('./pages/peticoes/PeticoesPage'));
const PeticaoDetailPage = lazy(() => import('./pages/peticoes/PeticaoDetailPage'));
const DelegacoesPage = lazy(() => import('./pages/delegacoes/DelegacoesPage'));
const TagsPage = lazy(() => import('./pages/tags/TagsPage'));
import NotFoundPage from './pages/NotFoundPage';
import SettingsPage from './pages/SettingsPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import SubscriptionPage from './pages/SubscriptionPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AuditoriaPage from './pages/admin/AuditoriaPage';
import CortesiaManagementPage from './pages/admin/CortesiaManagementPage';
import IaKeysPage from './pages/admin/IaKeysPage';
import ObservabilityPage from './pages/admin/ObservabilityPage';
import EmailQueuePage from './pages/admin/EmailQueuePage';
import JobsPage from './pages/admin/JobsPage';
import MonitoramentoPage from './pages/admin/MonitoramentoPage';
import OutboxPage from './pages/admin/OutboxPage';
import PushNotificationsPage from './pages/admin/PushNotificationsPage';
import PlanDetailPage from './pages/admin/PlanDetailPage';
import PlansManagementPage from './pages/admin/PlansManagementPage';
import CertificadosServicoPage from './pages/admin/CertificadosServicoPage';
import FontesTribunaisPage from './pages/admin/FontesTribunaisPage';
import CalendarioForensePage from './pages/admin/calendarioForense/CalendarioForensePage';
import TemplateEditorPage from './pages/admin/TemplateEditorPage';
import TemplatesPage from './pages/admin/TemplatesPage';
import UsuariosPage from './pages/admin/usuarios/UsuariosPage';
import WhitelistManagementPage from './pages/admin/whitelist/WhitelistManagementPage';
import ContactPage from './pages/public/ContactPage';
import FaqPage from './pages/public/FaqPage';
import {
  CookiesPage,
  LgpdPage,
  PrivacyPage,
  SobrePage,
  TermsPage,
} from './pages/PublicPages';

function AppRoutes() {
  const { isAuthenticated, isLoading, emailVerified, authError, authErrorTitulo, logout, whitelistBlocked, markInterestRegistered } = useAuth();

  // Usuário autenticado no Auth0 mas bloqueado pela whitelist de piloto.
  // Renderiza tela dedicada em cima de qualquer rota — não há menu/sidebar
  // acessível enquanto não for liberado.
  if (whitelistBlocked) {
    return (
      <BlockedPage
        email={whitelistBlocked.email}
        interestRegistered={whitelistBlocked.interestRegistered}
        onInterestRegistered={markInterestRegistered}
      />
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/sobre" element={<SobrePage />} />
      <Route path="/faq" element={<FaqPage />} />
      {/* Rota antiga: Central de Ajuda virou FAQ */}
      <Route path="/central-ajuda" element={<Navigate to="/faq" replace />} />
      <Route path="/contato" element={<ContactPage />} />
      <Route path="/termos" element={<TermsPage />} />
      <Route path="/privacidade" element={<PrivacyPage />} />
      <Route path="/lgpd" element={<LgpdPage />} />
      <Route path="/cookies" element={<CookiesPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/callback" element={<CallbackPage />} />
      <Route path="/verificar-email" element={<VerifyEmailPage />} />

      {/* Onboarding (protegido, mas sem Layout/sidebar) */}
      <Route
        path="/app/onboarding"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading} emailVerified={emailVerified} authError={authError} authErrorTitulo={authErrorTitulo} onLogout={logout}>
            <Suspense fallback={<FullPageLoading />}>
              <OnboardingPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/app"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading} emailVerified={emailVerified} authError={authError} authErrorTitulo={authErrorTitulo} onLogout={logout}>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="processos" element={<ProcessesPage />} />
        <Route path="processos/:numero/autos" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><Spinner size={32} label="Carregando autos" /></div>}><AutosPage /></Suspense>} />
        <Route path="processos/:numero/documentos/:uuid" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><Spinner size={32} label="Carregando documento" /></div>}><DocumentoPage /></Suspense>} />
        {/* Tela de peticionar aposentada: só redireciona aos autos (PLANO_BANCADA, Bloco 4). */}
        <Route path="processos/:numero/peticionar" element={<RedirecionarPeticionar />} />
        <Route path="processos/:numero/dossie" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><Spinner size={32} label="Carregando" /></div>}><DossiePage /></Suspense>} />
        <Route path="peticoes" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><Spinner size={32} label="Carregando" /></div>}><PeticoesPage /></Suspense>} />
        <Route path="peticoes/:id" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><Spinner size={32} label="Carregando" /></div>}><PeticaoDetailPage /></Suspense>} />
        <Route path="clientes" element={<ClientesPage />} />
        <Route path="clientes/novo" element={<ClienteFormPage />} />
        <Route path="clientes/:id/editar" element={<ClienteFormPage />} />
        <Route path="clientes/:id" element={<ClienteDetailPage />} />
        <Route path="documentos" element={<DocumentsPage />} />
        <Route path="calendario" element={<CalendarPage />} />
        <Route path="modelos" element={<ModelsPage />} />
        <Route path="modelos/novo" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><Spinner size={32} label="Carregando modelo" /></div>}><ModeloEditorPage /></Suspense>} />
        <Route path="modelos/:modeloId/editar" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><Spinner size={32} label="Carregando modelo" /></div>}><ModeloEditorPage /></Suspense>} />
        <Route path="tags" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><Spinner size={32} label="Carregando tags" /></div>}><TagsPage /></Suspense>} />
        <Route path="secretario-ia" element={<SecretarioPage />} />
        <Route path="secretario-ia/atendimentos/:id" element={<AtendimentoPage />} />
        <Route path="certificados" element={<CertificatesPage />} />
        <Route path="escritorios" element={<EscritoriosListaPage />} />
        <Route path="escritorios/:id" element={<EscritorioPage />} />
        <Route path="delegacoes" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><Spinner size={32} label="Carregando" /></div>}><DelegacoesPage /></Suspense>} />
        <Route path="comunicacoes" element={<ComunicacoesPage />} />
        <Route path="assinatura" element={<SubscriptionPage />} />
        <Route path="configuracoes" element={<SettingsPage />} />
        <Route path="configuracoes/timbrado" element={<TimbradoPage />} />
        <Route path="configuracoes/feriados" element={<FeriadosPage />} />
        <Route path="configuracoes/ia" element={<PersonalizacaoIaPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* Rotas administrativas */}
      <Route
        path="/app/admin"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading} emailVerified={emailVerified} authError={authError} authErrorTitulo={authErrorTitulo} onLogout={logout}>
            <RoleGuard roles={['admin', 'suporte']}>
              <AdminLayout />
            </RoleGuard>
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboardPage />} />
        <Route path="observabilidade" element={
          <RoleGuard roles={['admin']}>
            <ObservabilityPage />
          </RoleGuard>
        } />
        <Route path="planos" element={
          <RoleGuard roles={['admin']}>
            <PlansManagementPage />
          </RoleGuard>
        } />
        <Route path="planos/:id" element={
          <RoleGuard roles={['admin']}>
            <PlanDetailPage />
          </RoleGuard>
        } />
        <Route path="cortesia" element={
          <RoleGuard roles={['admin']}>
            <CortesiaManagementPage />
          </RoleGuard>
        } />
        <Route path="whitelist" element={
          <RoleGuard roles={['admin']}>
            <WhitelistManagementPage />
          </RoleGuard>
        } />
        <Route path="ia" element={
          <RoleGuard roles={['admin']}>
            <IaKeysPage />
          </RoleGuard>
        } />
        <Route path="monitoramento" element={
          <RoleGuard roles={['admin']}>
            <MonitoramentoPage />
          </RoleGuard>
        } />
        <Route path="jobs" element={
          <RoleGuard roles={['admin']}>
            <JobsPage />
          </RoleGuard>
        } />
        <Route path="outbox" element={
          <RoleGuard roles={['admin']}>
            <OutboxPage />
          </RoleGuard>
        } />
        <Route path="auditoria" element={
          <RoleGuard roles={['admin']}>
            <AuditoriaPage />
          </RoleGuard>
        } />
        <Route path="certificados-servico" element={
          <RoleGuard roles={['admin']}>
            <CertificadosServicoPage />
          </RoleGuard>
        } />
        <Route path="fontes-tribunais" element={
          <RoleGuard roles={['admin']}>
            <FontesTribunaisPage />
          </RoleGuard>
        } />
        <Route path="calendario-forense" element={
          <RoleGuard roles={['admin']}>
            <CalendarioForensePage />
          </RoleGuard>
        } />
        <Route path="templates" element={
          <RoleGuard roles={['admin']}>
            <TemplatesPage />
          </RoleGuard>
        } />
        <Route path="templates/:id" element={
          <RoleGuard roles={['admin']}>
            <TemplateEditorPage />
          </RoleGuard>
        } />
        <Route path="emails" element={
          <RoleGuard roles={['admin']}>
            <EmailQueuePage />
          </RoleGuard>
        } />
        <Route path="notificacoes" element={
          <RoleGuard roles={['admin']}>
            <PushNotificationsPage />
          </RoleGuard>
        } />
        <Route path="usuarios" element={
          <RoleGuard roles={['admin']}>
            <UsuariosPage />
          </RoleGuard>
        } />
        <Route path="*" element={<Navigate to="/app/admin" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <TooltipProvider delayDuration={300}>
      <NotificationProvider>
        <ClientSyncProvider>
        {/* Skip link para acessibilidade */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-0 focus:left-0 focus:z-[9999] focus:bg-[var(--color-primary)] focus:text-white focus:px-4 focus:py-2"
        >
          Pular para o conteúdo principal
        </a>
        <EnvironmentBanner />
        <OfflineBanner />
        <UpdateAvailable />
        <AppRoutes />
        <Toaster
          position="bottom-right"
          richColors
          closeButton
          toastOptions={{
            duration: 4000,
          }}
        />
        </ClientSyncProvider>
      </NotificationProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}

export default App;
